import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row } from 'react-bootstrap';
import CountUp from 'react-countup';
import useToken from 'hooks/useToken';
import { StatsCardSkeleton } from './SkeletonLoader';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserClock,
    faUserCheck,
    faUserPlus,
    faUsers
} from '@fortawesome/free-solid-svg-icons';

const StatCard = ({ title, value, color, icon }) => (
    <Card className="h-100 shadow-sm border-0">
        <Card.Body>
            <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                    <p className="text-muted mb-2 text-uppercase small fw-semibold">{title}</p>
                    <h4 className={`text-${color} mb-0 fw-bold text-nowrap`}>
                        <CountUp end={value || 0} duration={1.5} separator="," />
                    </h4>
                </div>
                {icon && (
                    <div className={`bg-${color} bg-opacity-10 p-3 rounded-3 d-flex align-items-center justify-content-center`}
                        style={{ width: '56px', height: '56px', minWidth: '56px' }}>
                        <FontAwesomeIcon icon={icon} className={`text-${color}`} style={{ fontSize: '24px' }} />
                    </div>
                )}
            </div>
        </Card.Body>
    </Card>
);

StatCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.number,
    color: PropTypes.string.isRequired,
    icon: PropTypes.object
};

const AkasiaPosUserStatsCards = ({ userTypeFilter = 'All' }) => {
    const { token } = useToken();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;

        const fetchStats = async () => {
            try {
                setLoading(true);

                const response = await fetch('/api/AkasiaPosDashboard/GetUserStatsCards', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ userType: userTypeFilter })
                });

                const result = await response.json();

                if (result.iResponseType === 1 && result.detalles) {
                    const parsed = JSON.parse(result.detalles);
                    const statsData = parsed.table2?.[0] || parsed.table1?.[0];
                    setStats(statsData);
                } else {
                    setError('Error loading stats');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [token, userTypeFilter]); // Re-fetch when token or userTypeFilter changes

    if (loading) {
        return <StatsCardSkeleton count={4} />;
    }

    if (error || !stats) {
        return (
            <div className="text-center py-5">
                <i className="bi bi-exclamation-circle text-danger fs-1"></i>
                <p className="text-danger mt-2">{error || 'No se pudieron cargar las estadísticas'}</p>
            </div>
        );
    }

    return (
        <Row className="g-3 mb-3">
            <Col md={3} sm={6}>
                <StatCard
                    title="Usuarios Trial"
                    value={stats.iUsuariosTrial}
                    color="warning"
                    icon={faUserClock}
                />
            </Col>
            <Col md={3} sm={6}>
                <StatCard
                    title="Usuarios Pagados"
                    value={stats.iUsuariosPagados}
                    color="success"
                    icon={faUserCheck}
                />
            </Col>
            <Col md={3} sm={6}>
                <StatCard
                    title="Usuarios Activos Hoy"
                    value={stats.iUsuariosActivos}
                    color="info"
                    icon={faUserPlus}
                />
            </Col>
            <Col md={3} sm={6}>
                <StatCard
                    title="Total Usuarios"
                    value={stats.iUsuariosTotal}
                    color="dark"
                    icon={faUsers}
                />
            </Col>
        </Row>
    );
};

AkasiaPosUserStatsCards.propTypes = {
    userTypeFilter: PropTypes.string
};

export default AkasiaPosUserStatsCards;
